import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, Linkedin, Instagram, MessageCircle, Send } from 'lucide-react';
import { personalInfo } from '../../mock';

const BoldContactSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const buttonRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus({ 
          type: 'success', 
          message: 'Message sent successfully! I\'ll get back to you soon.' 
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        setFormStatus({ 
          type: 'error', 
          message: data.message || 'Something went wrong. Please try again.' 
        });
      }
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-32 px-6 relative">
      <div className="section-watermark" style={{ top: '5%', left: '-5%' }}>07</div>
      
      <div className="max-w-4xl mx-auto" ref={ref}>
        {/* Statement Heading */}
        <motion.h2
          className="text-center mb-6 gradient-text"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          Let's build something<br />that works
        </motion.h2>
        
        <motion.p
          className="text-center text-[#9D97B5] text-xl mb-16"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
        >
          Got a project or just want to talk strategy? I reply fast.
        </motion.p>
        
        {/* Asymmetric Layout - Contact Info Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
        >
          <motion.a
            href={`tel:${personalInfo.phone}`}
            className="p-8 bg-[#1A1430] border-2 border-[#7C3AED]/20 rounded-3xl hover:border-[#FF5C39] transition-all duration-300 group"
            whileHover={{ scale: 1.03, y: -5 }}
          >
            <Phone size={32} className="text-[#7C3AED] mb-4 group-hover:text-[#FF5C39] transition-colors" />
            <p className="text-sm text-[#9D97B5] mb-2">Phone</p>
            <p className="text-xl font-bold text-[#EDE9FE]">{personalInfo.phone}</p>
          </motion.a>
          
          <motion.a
            href={`mailto:${personalInfo.email}`}
            className="p-8 bg-[#1A1430] border-2 border-[#7C3AED]/20 rounded-3xl hover:border-[#FF5C39] transition-all duration-300 group"
            whileHover={{ scale: 1.03, y: -5 }}
          >
            <Mail size={32} className="text-[#7C3AED] mb-4 group-hover:text-[#FF5C39] transition-colors" />
            <p className="text-sm text-[#9D97B5] mb-2">Email</p>
            <p className="text-xl font-bold text-[#EDE9FE] break-all">{personalInfo.email}</p>
          </motion.a>
        </motion.div>
        
        {/* Contact Form with Glowing Fields */}
        <motion.div
          className="bg-[#1A1430] border-2 border-[#7C3AED]/20 rounded-3xl p-10"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.6 }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Name"
                required
                className="w-full px-6 py-5 bg-[#0E0B1F] border-2 border-[#7C3AED]/20 rounded-2xl text-[#EDE9FE] placeholder-[#9D97B5] focus:outline-none focus:border-[#FF5C39] focus:shadow-lg focus:shadow-[#FF5C39]/30 transition-all text-lg"
              />
            </div>
            
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Your Email"
                required
                className="w-full px-6 py-5 bg-[#0E0B1F] border-2 border-[#7C3AED]/20 rounded-2xl text-[#EDE9FE] placeholder-[#9D97B5] focus:outline-none focus:border-[#FF5C39] focus:shadow-lg focus:shadow-[#FF5C39]/30 transition-all text-lg"
              />
            </div>
            
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Your Message"
                rows="6"
                required
                className="w-full px-6 py-5 bg-[#0E0B1F] border-2 border-[#7C3AED]/20 rounded-2xl text-[#EDE9FE] placeholder-[#9D97B5] focus:outline-none focus:border-[#FF5C39] focus:shadow-lg focus:shadow-[#FF5C39]/30 resize-none transition-all text-lg"
              />
            </div>
            
            {formStatus.message && (
              <motion.div
                className={`p-5 rounded-2xl ${
                  formStatus.type === 'success' 
                    ? 'bg-[#22D3A5]/10 border-2 border-[#22D3A5]/30 text-[#22D3A5]' 
                    : 'bg-[#FF5C39]/10 border-2 border-[#FF5C39]/30 text-[#FF5C39]'
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {formStatus.message}
              </motion.div>
            )}
            
            {/* Magnetic Submit Button */}
            <motion.button
              ref={buttonRef}
              type="submit"
              disabled={isSubmitting}
              className="w-full px-10 py-6 bg-gradient-to-r from-[#FF5C39] to-[#C026D3] text-white rounded-full font-bold text-xl hover:shadow-2xl hover:shadow-[#FF5C39]/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all duration-300"
              style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
              whileHover={{ scale: 1.03, y: -3 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? 'Sending...' : (
                <>
                  Send it
                  <Send size={24} />
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
        
        {/* Social Links */}
        <motion.div
          className="flex gap-6 justify-center mt-16"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          {[
            { icon: Linkedin, href: 'https://www.linkedin.com/in/maheen-kehkasha/', color: '#7C3AED', label: 'LinkedIn' },
            { icon: Instagram, href: 'https://www.instagram.com/websbymahi/', color: '#FF5C39', label: 'Instagram' },
            { icon: MessageCircle, href: personalInfo.whatsappLink, color: '#22D3A5', label: 'WhatsApp' }
          ].map(({ icon: Icon, href, color, label }) => (
            <motion.a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 rounded-full bg-[#1A1430] border-2 border-[#7C3AED]/20 flex items-center justify-center text-[#9D97B5] hover:border-[#FF5C39] transition-all duration-300"
              aria-label={label}
              whileHover={{ scale: 1.2, rotate: 10 }}
            >
              <Icon size={24} />
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default BoldContactSection;